/**
 * Schedule/agenda view — chronological meeting list grouped by date.
 *
 * Shows upcoming meetings from today forward by default, with an option
 * to reveal past meetings from earlier in the month.
 *
 * Paginated (20 items per page) with capped animation delay.
 *
 * @module components/calendar/ScheduleView
 */

import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { format, parseISO, startOfMonth, startOfDay, endOfMonth } from 'date-fns'
import { utcToZonedTime } from 'date-fns-tz'
import { motion } from 'framer-motion'
import { Calendar as CalendarIcon, Clock, Users, Plus, ChevronUp } from 'lucide-react'
import {
  groupEventsByDay,
  getStatusColor,
  getMeetingTitle,
  isSameDayTz,
} from '../../utils/calendarLayout'

const ITEMS_PER_PAGE = 20

function DateHeader({ dateStr, userTz }) {
  const date = parseISO(dateStr)
  const today = new Date()
  const tomorrow = new Date(today)
  tomorrow.setDate(tomorrow.getDate() + 1)

  let prefix = ''
  if (isSameDayTz(date, today, userTz)) prefix = 'Today — '
  else if (isSameDayTz(date, tomorrow, userTz)) prefix = 'Tomorrow — '

  return (
    <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3 mt-6 first:mt-0">
      {prefix}
      {format(utcToZonedTime(date, userTz), 'EEEE, MMMM d')}
    </h3>
  )
}

function MeetingCard({ meeting, userTz, animIndex }) {
  const colors = getStatusColor(meeting.status, meeting.isDraft)
  const title = getMeetingTitle(meeting)
  const startStr = meeting.startTime || meeting.scheduledStartTime

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: Math.min(animIndex * 0.03, 0.5), duration: 0.25 }}
    >
      <Link
        to={`/meeting/${meeting.id ?? meeting.meetingId}`}
        className={`block p-4 rounded-xl bg-gray-50 border border-gray-100 hover:border-gray-200 transition-all ${
          meeting.isDraft ? 'border-dashed opacity-80' : ''
        }`}
      >
        <div className="flex items-start gap-3">
          <div className={`w-1 h-12 rounded-full shrink-0 ${colors.dot}`} />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <h4 className="font-medium text-gray-900 truncate">{title}</h4>
              {/* Status badge */}
              <span
                className={`text-[10px] px-1.5 py-0.5 rounded-full shrink-0 ${colors.bg} ${colors.text}`}
              >
                {meeting.status}
              </span>
              {meeting.isDraft && (
                <span className="text-[10px] px-1.5 py-0.5 rounded border border-dashed border-gray-300 text-gray-400 shrink-0">
                  Draft
                </span>
              )}
            </div>
            <div className="flex items-center gap-4 text-sm text-gray-500">
              {startStr && (
                <span className="flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  {new Intl.DateTimeFormat('en-US', {
                    timeZone: userTz,
                    hour: 'numeric',
                    minute: '2-digit',
                    hour12: true,
                    timeZoneName: 'shortGeneric',
                  }).format(parseISO(startStr))}
                </span>
              )}
              <span className="flex items-center gap-1">
                <Users className="w-3 h-3" />
                {meeting.participantCount ??
                  meeting.participants?.length ??
                  0}{' '}
                participants
              </span>
              {meeting.agendaCount > 0 && (
                <span className="text-xs text-gray-400">
                  {meeting.agendaCount} agenda item{meeting.agendaCount > 1 ? 's' : ''}
                </span>
              )}
            </div>
          </div>
        </div>
      </Link>
    </motion.div>
  )
}

export default function ScheduleView({ meetings, currentDate, userTz }) {
  const [visibleCount, setVisibleCount] = useState(ITEMS_PER_PAGE)
  const [showPast, setShowPast] = useState(false)

  const dateInTz = utcToZonedTime(currentDate, userTz)
  const todayInTz = utcToZonedTime(new Date(), userTz)
  const todayStart = startOfDay(todayInTz)

  // Split meetings into upcoming (today+) and past (before today) within the month
  const { upcomingFlat, pastFlat, pastCount, upcomingCount } = useMemo(() => {
    const ms = startOfMonth(dateInTz)
    const me = endOfMonth(dateInTz)
    const monthMeetings = meetings.filter((m) => {
      const s = m.startTime ?? m.scheduledStartTime
      if (!s) return false
      const d = utcToZonedTime(parseISO(s), userTz)
      return d >= ms && d <= me
    })

    const upcoming = []
    const past = []
    for (const m of monthMeetings) {
      const s = m.startTime ?? m.scheduledStartTime
      const d = utcToZonedTime(parseISO(s), userTz)
      if (d >= todayStart) {
        upcoming.push(m)
      } else {
        past.push(m)
      }
    }

    // Group and flatten each set
    function flattenGrouped(list) {
      const dm = groupEventsByDay(list, userTz)
      const keys = [...dm.keys()].sort()
      const result = []
      for (const key of keys) {
        for (const m of dm.get(key)) {
          result.push({ dateKey: key, meeting: m })
        }
      }
      return result
    }

    return {
      upcomingFlat: flattenGrouped(upcoming),
      pastFlat: flattenGrouped(past),
      pastCount: past.length,
      upcomingCount: upcoming.length,
    }
  }, [meetings, dateInTz, todayStart, userTz])

  // Combine: past (if revealed) + upcoming
  const flatMeetings = useMemo(() => {
    return showPast ? [...pastFlat, ...upcomingFlat] : upcomingFlat
  }, [showPast, pastFlat, upcomingFlat])

  const visible = flatMeetings.slice(0, visibleCount)
  const hasMore = visibleCount < flatMeetings.length

  // Group visible items back by date for rendering
  let lastDateKey = null
  let animIndex = 0

  return (
    <div className="glass rounded-2xl p-6">
      {/* Show earlier meetings toggle */}
      {pastCount > 0 && !showPast && (
        <button
          type="button"
          onClick={() => setShowPast(true)}
          className="w-full py-2.5 mb-4 rounded-lg text-sm font-medium text-gray-500 hover:text-gray-700 hover:bg-gray-50 transition-colors flex items-center justify-center gap-1.5"
        >
          <ChevronUp className="w-4 h-4" />
          Show {pastCount} earlier meeting{pastCount !== 1 ? 's' : ''}
        </button>
      )}

      {visible.length > 0 ? (
        <div>
          {visible.map((item, i) => {
            const showHeader = item.dateKey !== lastDateKey
            lastDateKey = item.dateKey
            const idx = animIndex++

            return (
              <div key={`${item.dateKey}-${item.meeting.id ?? item.meeting.meetingId ?? i}`}>
                {showHeader && <DateHeader dateStr={item.dateKey} userTz={userTz} />}
                <div className="mb-3">
                  <MeetingCard
                    meeting={item.meeting}
                    userTz={userTz}
                    animIndex={idx}
                  />
                </div>
              </div>
            )
          })}

          {hasMore && (
            <button
              type="button"
              onClick={() => setVisibleCount((c) => c + ITEMS_PER_PAGE)}
              className="w-full py-3 mt-2 rounded-lg text-sm font-medium text-primary-600 hover:bg-primary-50 transition-colors"
            >
              Load more ({flatMeetings.length - visibleCount} remaining)
            </button>
          )}
        </div>
      ) : (
        <div className="empty-state">
          <CalendarIcon className="empty-state-icon" />
          <h3 className="empty-state-title">No upcoming meetings</h3>
          <p className="empty-state-text">
            There are no meetings scheduled from today through{' '}
            {format(endOfMonth(dateInTz), 'MMMM d, yyyy')}.
          </p>
          <Link
            to="/agenda"
            className="btn-secondary text-sm inline-flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            Create Meeting
          </Link>
        </div>
      )}

      {(upcomingCount > 0 || pastCount > 0) && (
        <p className="text-xs text-gray-400 text-center mt-4">
          {upcomingCount} upcoming{pastCount > 0 ? `, ${pastCount} past` : ''} in{' '}
          {format(dateInTz, 'MMMM yyyy')}
        </p>
      )}
    </div>
  )
}
